import { ParentDepsKey, yup } from '@framework/validation';
import { FormValidatorBuilder, getWidgetName } from '../../../../../validator';
import { getParentValidationScope } from '../../../../../validator/utils/getParentValidationScope';
import { remapReferencesUpwardInScope } from '../../../../../validator/utils/remapReferencesUpwardInScope';
import { IParentWidgetScope, IWidgetType } from '../../../IWidgetType';
import { IRepeatableWidget } from './IRepeatableWidget';

export function createRepeatableChildrenSchema(
  widget: IRepeatableWidget,
  section: number,
  page: number,
  parentScope: IParentWidgetScope,
) {
  const { children } = widget;
  const childrenSchema = FormValidatorBuilder.createWidgetsSchemaShape(
    children,
    section,
    page,
    parentScope,
  );
  delete childrenSchema.__isWidgetSchemaShape;
  return yup.object().shape(childrenSchema);
}

/**
 * Funzione che crea lo schema di validazione per un widget di tipo Repeatable.
 * Il risultato è un oggetto { [fielName]: arraySchema } costruito ricorsivamente su
 * tutti i campi all'interno.
 */
export const createRepeatableSchema: IWidgetType<IRepeatableWidget>['createFormSchema'] =
  ({ widget, section, page, parentScope }) => {
    // Recupero lo scope di validazione del widget lista ripetibile da passare ai children
    const widgetScope = getParentValidationScope(widget, parentScope);
    const fieldName = getWidgetName(widget);
    const childrenSchema = createRepeatableChildrenSchema(
      widget,
      section,
      page,
      // Le referenze all'interno dei figli devono essere riferite al widget
      // padre, quindi le rimappiamo
      remapReferencesUpwardInScope(widget, widgetScope),
    );

    // Se è definito un minimo di ripetizioni, allora il valore di default è un array con il default dei campi figli
    const repeatRule = widget.rules?.repeatableRange;

    let defaultValue: any[];

    if (repeatRule?.min) {
      defaultValue = Array.from({ length: repeatRule.min }, () =>
        childrenSchema.cast({}, { assert: false }),
      );
    } else {
      // Se è richiesto di "mostrare almeno un elemento" allora ne inserisco solo uno
      defaultValue = widget.layout?.showAtLeastOne
        ? [childrenSchema.cast({}, { assert: false })]
        : [];
    }

    // TODO REFATTORIZZARE
    // Recupera dallo schema dei figli le dipendenze dei when annidati
    // per applicarle alla lista così da poter far sì che yup ordini
    // correttamente i cast (e non arrivino undefined con campi in realtà valorizzati)
    const childrenDeps: string[] = Object.values(
      childrenSchema.fields ?? {},
    ).flatMap(field => {
      const schema = field as any;
      return (
        schema.spec?.meta?.[ParentDepsKey]?.map((dep: string) =>
          dep?.split('.').slice(1).join('.'),
        ) ?? []
      );
    });

    const arraySchema = yup
      .array()
      .of(childrenSchema)
      .parentWhen(childrenDeps, (_, schema) => schema)
      .required()
      // TODO Rimuovere o gestire diversamente, va in conflitto con il `defaultValue`
      .default(defaultValue);

    return {
      [fieldName]: arraySchema,
    };
  };
